import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import DataTab from '@rio-cloud/rio-uikit/DataTab';
import DataTabs from '@rio-cloud/rio-uikit/DataTabs';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tag from '@rio-cloud/rio-uikit/Tag';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import { gaPush } from '@/configuration/setup/googleAnalytics';
import { isManSCLBrakeWearFeatureToggleEnabled } from '@/features/app/featureToggles';
import { CustomDateTime } from '@/utils/DateTimeUtils';
import { ColorMappings } from './ColorMappings';
import { VehicleStatusDataSectionKey, VehicleStatusDataSections } from './DataTabsSettingsDialogComponent';
import NextAppointmentTooltipComponent from './NextAppointmentTooltipComponent';

const HIGHLIGHT_DURATION_SECONDS = 2;

type VehicleDataTabComponentProps = {
    type?: string;
    name?: string;
    vin?: string;
    customerName?: string;
    licensePlate?: string;
    deviceType?: string;
    bookedProduct?: string;
    totalAppointments?: number;
    totalWorkshopActions?: number;
    totalVehicleNotes?: number;
    nextWorkshopAppointment?: string;
    totalComponentsOverdue?: number;
    totalComponentsAlmostDue?: number;
    totalBrakeLiningsCritical?: number;
    totalBrakeLiningsWarning?: number;
    totalActiveDm1DtcsCritical?: number;
    totalActiveDm1DtcsWarning?: number;
    totalActiveDm1DtcsOther?: number;
    totalActiveDm4DtcsCritical?: number;
    totalActiveDm4DtcsWarning?: number;
    totalActiveDm4DtcsOther?: number;
    totalWarningMessagesCritical?: number;
    totalWarningMessagesWarning?: number;
    totalWarningMessagesInfo?: number;
    lastMaintenanceMessageTimestamp?: string;
    onClickVehicleInfoSection: () => void;
    onClickMaintenanceSection: () => void;
    onClickBrakeLiningsSection: () => void;
    onClickWarningMessagesSection: () => void;
    onClickDtcDm1Section: () => void;
    onClickDtcDm4Section: () => void;
    onClickVehicleNameAndVinAndLicensePlate: () => void;
    onClickCustomerName: () => void;
    syncStatus: string;
    status: string;
    highlight: boolean;
    clearHighlight: () => void;
    selectedDataTabsSections: VehicleStatusDataSectionKey[];
};

type VehicleBasicDataComponentProps = {
    type?: string;
    name?: string;
    bookedProduct?: string;
    vin?: string;
    customerName?: string;
    licensePlate?: string;
    lastMaintenanceMessageTimestamp?: string;
    syncStatus: string;
    onClickVehicleNameAndVinAndLicensePlate: () => void;
    onClickCustomerName: () => void;
    highlight: boolean;
};

type VehicleDataComponentProps = {
    deviceType?: string;
    totalAppointments?: number;
    totalWorkshopActions?: number;
    totalVehicleNotes?: number;
    nextWorkshopAppointment?: string;
    totalComponentsOverdue?: number;
    totalComponentsAlmostDue?: number;
    totalBrakeLiningsCritical?: number;
    totalBrakeLiningsWarning?: number;
    totalActiveDm1DtcsCritical?: number;
    totalActiveDm1DtcsWarning?: number;
    totalActiveDm1DtcsOther?: number;
    totalActiveDm4DtcsCritical?: number;
    totalActiveDm4DtcsWarning?: number;
    totalActiveDm4DtcsOther?: number;
    totalWarningMessagesCritical?: number;
    totalWarningMessagesWarning?: number;
    totalWarningMessagesInfo?: number;
    onClickVehicleInfoSection: () => void;
    onClickMaintenanceSection: () => void;
    onClickBrakeLiningsSection: () => void;
    onClickWarningMessagesSection: () => void;
    onClickDtcDm1Section: () => void;
    onClickDtcDm4Section: () => void;
    highlight: boolean;
    selectedDataTabsSections: VehicleStatusDataSectionKey[];
};

type dataTabSectionDetailsComponentProps = {
    dataTabSectionDetailsTitle: string;
    totalCriticalItems?: number;
    totalWarningItems?: number;
    totalOtherItems?: number;
};

const getIconByVehicleType = (type: string | undefined) => {
    if (type?.toUpperCase() === 'TRUCK' || type?.toUpperCase() === 'BUS' || type?.toUpperCase() === 'VAN') {
        return `rioglyph rioglyph-${type.toLowerCase()}`;
    }
};

const getSyncStatusColor = (syncStatus: string) => {
    switch (syncStatus) {
        case 'NOT_SYNCED':
            return {
                syncStatusColor: 'danger',
                syncStatusMessage: 'intl-msg:vehicle_status.datatabs.vehicle_info.sync_status.not_synced',
            };
        case 'RECENTLY_SYNCED':
            return {
                syncStatusColor: 'warning',
                syncStatusMessage: 'intl-msg:vehicle_status.datatabs.vehicle_info.sync_status.recently_synced',
            };
        case 'SYNCED':
            return {
                syncStatusColor: 'success',
                syncStatusMessage: 'intl-msg:vehicle_status.datatabs.vehicle_info.sync_status.synced',
            };
    }
};

const syncStatusIcon = (lastMaintenanceMessageTimestamp: string | undefined, syncStatus: string) => {
    const lastUpdateStatusMessage = 'intl-msg:vehicle_status.datatabs.vehicle_info.sync_status.maintenance_last_update';
    const { syncStatusColor, syncStatusMessage } = getSyncStatusColor(syncStatus)!;
    return (
        <OverlayTrigger
            placement={Tooltip.RIGHT}
            overlay={
                <Tooltip allowOnTouch textAlignment="left" width={200}>
                    <span className="text-bold">
                        <FormattedMessage id={syncStatusMessage} />
                    </span>
                    {lastMaintenanceMessageTimestamp && (
                        <>
                            <br />
                            <FormattedMessage id={lastUpdateStatusMessage} />
                            <span className="padding-left-5">
                                <CustomDateTime value={lastMaintenanceMessageTimestamp} showWeekDay={false} />
                            </span>
                        </>
                    )}
                </Tooltip>
            }
        >
            <span className={`rioglyph rioglyph-refresh text-size-16 text-color-${syncStatusColor}`} />
        </OverlayTrigger>
    );
};

const getStatusColorClassName = (status: string) => {
    switch (status) {
        case 'CRITICAL':
            return ColorMappings.CRITICAL.backgroundColorClassName;
        case 'WARNING':
            return ColorMappings.WARNING.backgroundColorClassName;
        case 'OK':
            return ColorMappings.OK.backgroundColorClassName;
        default:
            return ColorMappings.UNKNOWN.backgroundColorClassName;
    }
};

const getBookedProductLevelTag = (product: string | undefined) => {
    switch (product) {
        case 'SERVICE_CARE_L':
            return 'L';
        case 'SERVICE_CARE_M':
            return 'M';
        case 'SERVICE_CARE_S':
            return 'S';
        default:
            return '?';
    }
};

const VehicleBasicDataComponent = ({
    type,
    name,
    bookedProduct,
    vin,
    customerName,
    licensePlate,
    lastMaintenanceMessageTimestamp,
    syncStatus,
    onClickVehicleNameAndVinAndLicensePlate,
    onClickCustomerName,
    highlight,
}: VehicleBasicDataComponentProps) => (
    <div
        className={
            'display-flex justify-content-start align-items-center gap-20 padding-10' +
            ' flex-1-0 margin-top-20 border border-bottom-none' +
            (highlight ? ' bg-light border-color-dark' : ' bg-lighter')
        }
        data-testid="vehicle-basic-data"
    >
        <div data-testid="vehicle-basic-data-type-icon">
            <span className={`${getIconByVehicleType(type)} text-size-16`} />
        </div>
        <div>{syncStatusIcon(lastMaintenanceMessageTimestamp, syncStatus)}</div>
        <div data-testid="vehicle-basic-data-name">
            <span className="margin-right-10 text-color-highlight-light">
                <FormattedMessage id="intl-msg:vehicle_status.datatabs.vehicle_info.name" />
            </span>
            <a
                className="hover-text-decoration-none"
                onClick={() => {
                    gaPush({
                        event: 'vs_vehicle_name',
                        parameters: {
                            element_name: 'VehicleBasicDataComponent',
                            component_name: 'Button',
                            trigger: 'click',
                        },
                    });
                    onClickVehicleNameAndVinAndLicensePlate();
                }}
            >
                {name}
            </a>
            {isManSCLBrakeWearFeatureToggleEnabled() && (
                <Tag
                    size=""
                    round={false}
                    className={
                        'margin-left-10 ' +
                        'text-size-12 text-color-primary ' +
                        'border border-color-primary ' +
                        'padding-y-4 padding-x-10 ' +
                        'rounded-small ' +
                        'bg-lighter'
                    }
                >
                    {getBookedProductLevelTag(bookedProduct)}
                </Tag>
            )}
        </div>
        <div data-testid="vehicle-basic-data-vin">
            <span className="margin-right-10 text-color-highlight-light">
                <FormattedMessage id="intl-msg:vehicle_status.datatabs.vehicle_info.vin" />
            </span>
            <a
                className="hover-text-decoration-none"
                onClick={() => {
                    gaPush({
                        event: 'vs_vehicle_vin',
                        parameters: {
                            element_name: 'VehicleBasicDataComponent',
                            component_name: 'Button',
                            trigger: 'click',
                        },
                    });
                    onClickVehicleNameAndVinAndLicensePlate();
                }}
            >
                {vin}
            </a>
        </div>
        <div data-testid="vehicle-basic-data-customer_name">
            <span className="margin-right-10 text-color-highlight-light">
                <FormattedMessage id="intl-msg:vehicle_status.datatabs.vehicle_info.customer_name" />
            </span>
            <a
                className="hover-text-decoration-none"
                onClick={() => {
                    gaPush({
                        event: 'vs_customer_name',
                        parameters: {
                            element_name: 'VehicleBasicDataComponent',
                            component_name: 'Button',
                            trigger: 'click',
                        },
                    });
                    onClickCustomerName();
                }}
            >
                {customerName}
            </a>
        </div>
        <div data-testid="vehicle-basic-data-license-plate">
            <span className="margin-right-10 text-color-highlight-light">
                <FormattedMessage id="intl-msg:vehicle_status.datatabs.vehicle_info.license_plate" />
            </span>
            <a
                className="hover-text-decoration-none"
                onClick={() => {
                    gaPush({
                        event: 'vs_license_plate',
                        parameters: {
                            element_name: 'VehicleBasicDataComponent',
                            component_name: 'Button',
                            trigger: 'click',
                        },
                    });
                    onClickVehicleNameAndVinAndLicensePlate();
                }}
            >
                {licensePlate || (
                    <FormattedMessage id="intl-msg:vehicle_status.datatabs.vehicle_info.license_plate.not_added" />
                )}
            </a>
        </div>
    </div>
);

const countItems = (totalItems?: number) => (totalItems === undefined ? 0 : totalItems);

const DataTabSectionDetails = ({
    dataTabSectionDetailsTitle,
    totalCriticalItems,
    totalWarningItems,
    totalOtherItems,
}: dataTabSectionDetailsComponentProps) => {
    const noDataReceived =
        totalCriticalItems === undefined && totalWarningItems === undefined && totalOtherItems === undefined;
    const areAllItemsOk =
        countItems(totalCriticalItems) + countItems(totalWarningItems) + countItems(totalOtherItems) === 0;
    return (
        <>
            <FormattedMessage id={dataTabSectionDetailsTitle} />

            <div className="display-flex align-content-start padding-top-10 gap-10 flex-1-0">
                {noDataReceived ? (
                    <span className="text-color-highlight-light text-size-12">
                        <FormattedMessage id="intl-msg:vehicle_status.datatabs.vehicle_data.no_data" />
                    </span>
                ) : (
                    <>
                        {areAllItemsOk && (
                            <span
                                className="margin-right-5 rioglyph rioglyph-ok text-size-16"
                                data-testid="details-for-data-tabs-icon"
                            />
                        )}
                        {totalCriticalItems !== undefined && totalCriticalItems > 0 && (
                            <div>
                                <span
                                    className="
                                        margin-right-5 rioglyph rioglyph-error-sign text-color-danger text-size-16
                                    "
                                    data-testid="details-for-data-tabs-icon"
                                />
                                {totalCriticalItems}
                            </div>
                        )}
                        {totalWarningItems !== undefined && totalWarningItems > 0 && (
                            <div>
                                <span
                                    className="
                                        margin-right-5 rioglyph rioglyph-warning-sign text-color-warning text-size-16
                                    "
                                    data-testid="details-for-data-tabs-icon"
                                />
                                {totalWarningItems}
                            </div>
                        )}
                        {totalOtherItems !== undefined && totalOtherItems > 0 && (
                            <div>
                                <span
                                    className="margin-right-5 rioglyph rioglyph-ongoing text-color-gray text-size-16"
                                    data-testid="details-for-data-tabs-icon"
                                />
                                {totalOtherItems}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

const VehicleDataComponent = ({
    deviceType,
    totalAppointments,
    totalWorkshopActions,
    totalVehicleNotes,
    nextWorkshopAppointment,
    totalComponentsOverdue,
    totalComponentsAlmostDue,
    totalBrakeLiningsCritical,
    totalBrakeLiningsWarning,
    totalActiveDm1DtcsCritical,
    totalActiveDm1DtcsWarning,
    totalActiveDm1DtcsOther,
    totalActiveDm4DtcsCritical,
    totalActiveDm4DtcsWarning,
    totalActiveDm4DtcsOther,
    totalWarningMessagesCritical,
    totalWarningMessagesWarning,
    totalWarningMessagesInfo,
    onClickVehicleInfoSection,
    onClickMaintenanceSection,
    onClickBrakeLiningsSection,
    onClickWarningMessagesSection,
    onClickDtcDm1Section,
    onClickDtcDm4Section,
    selectedDataTabsSections,
}: VehicleDataComponentProps) => {
    const onClickFunctions: Record<string, () => void> = {
        'workshop-details-tab': () => {
            gaPush({
                event: 'column_vehicle_info',
                parameters: {
                    element_name: 'VehicleDataComponent',
                    component_name: 'DataTabs',
                    trigger: 'click',
                },
            });
            onClickVehicleInfoSection();
        },
        'maintenance-details-tab': () => {
            gaPush({
                event: 'column_maintenance',
                parameters: {
                    element_name: 'VehicleDataComponent',
                    component_name: 'DataTabs',
                    trigger: 'click',
                },
            });
            onClickMaintenanceSection();
        },
        'brake-linings-details-tab': () => {
            gaPush({
                event: 'column_brake_linings',
                parameters: {
                    element_name: 'VehicleDataComponent',
                    component_name: 'DataTabs',
                    trigger: 'click',
                },
            });
            onClickBrakeLiningsSection();
        },
        'warning-messages-details-tab': () => {
            gaPush({
                event: 'column_warning_messages',
                parameters: {
                    element_name: 'VehicleDataComponent',
                    component_name: 'DataTabs',
                    trigger: 'click',
                },
            });
            onClickWarningMessagesSection();
        },
        'dtc-dm4-details-tab': () => {
            gaPush({
                event: 'column_dtc_memory',
                parameters: {
                    element_name: 'VehicleDataComponent',
                    component_name: 'DataTabs',
                    trigger: 'click',
                },
            });
            onClickDtcDm4Section();
        },
        'dtc-dm1-details-tab': () => {
            gaPush({
                event: 'column_dtc_active',
                parameters: {
                    element_name: 'VehicleDataComponent',
                    component_name: 'DataTabs',
                    trigger: 'click',
                },
            });
            onClickDtcDm1Section();
        },
    };
    return (
        <DataTabs
            tabActiveClassName=""
            tabContentClassName="display-none"
            arrowClassName="none"
            activeKey="hide-tab"
            onSelectTab={(selectedTab: string) => onClickFunctions[selectedTab]()}
        >
            {/* This is a hidden DataTab because the DataTabs always need to have one active tab */}
            <DataTab tabKey="hide-tab" title="" className="display-none width-0" />
            <DataTab
                tabKey="workshop-details-tab"
                title={
                    <>
                        <span>
                            <FormattedMessage id="intl-msg:vehicle_status.datatabs.workshop_details.vehicle_info" />
                            <NextAppointmentTooltipComponent nextWorkshopAppointment={nextWorkshopAppointment} />
                        </span>
                        <div className="display-flex align-content-start padding-top-10 gap-10 flex-1-0">
                            <div>
                                <span className="margin-right-5 rioglyph rioglyph-calendar text-size-16" />
                                <span>{totalAppointments}</span>
                            </div>
                            <div>
                                <span className="margin-right-5 rioglyph rioglyph-workshop text-size-16" />
                                <span>{totalWorkshopActions}</span>
                            </div>
                            <div>
                                <span className="margin-right-5 rioglyph rioglyph-comment text-size-16" />
                                <span>{totalVehicleNotes}</span>
                            </div>
                        </div>
                    </>
                }
            />
            {selectedDataTabsSections.includes(VehicleStatusDataSections.MAINTENANCE) && (
                <DataTab
                    tabKey="maintenance-details-tab"
                    className="border border-left-only"
                    title={
                        <DataTabSectionDetails
                            dataTabSectionDetailsTitle="intl-msg:vehicle_status.datatabs.maintenance_details.maintenance"
                            totalCriticalItems={totalComponentsOverdue}
                            totalWarningItems={totalComponentsAlmostDue}
                        />
                    }
                />
            )}
            {selectedDataTabsSections.includes(VehicleStatusDataSections.BRAKE_LININGS) && (
                <DataTab
                    tabKey="brake-linings-details-tab"
                    className="border border-left-only"
                    title={
                        <DataTabSectionDetails
                            dataTabSectionDetailsTitle="intl-msg:vehicle_status.datatabs.brake_linings_details.brake_linings"
                            totalCriticalItems={totalBrakeLiningsCritical}
                            totalWarningItems={totalBrakeLiningsWarning}
                        />
                    }
                />
            )}
            {deviceType === 'ocu3' && selectedDataTabsSections.includes(VehicleStatusDataSections.WARNING_MESSAGES) && (
                <DataTab
                    tabKey="warning-messages-details-tab"
                    className="border border-left-only"
                    title={
                        <DataTabSectionDetails
                            dataTabSectionDetailsTitle={
                                'intl-msg:vehicle_status.datatabs.warning_messages_details.warning_messages'
                            }
                            totalCriticalItems={totalWarningMessagesCritical}
                            totalWarningItems={totalWarningMessagesWarning}
                            totalOtherItems={totalWarningMessagesInfo}
                        />
                    }
                />
            )}
            {(deviceType === 'tbm3' || deviceType === 'cm4') &&
                selectedDataTabsSections.includes(VehicleStatusDataSections.DTC_DIAGNOSTIC_MEMORY) && (
                    <DataTab
                        tabKey="dtc-dm4-details-tab"
                        className="border border-left-only"
                        title={
                            <DataTabSectionDetails
                                dataTabSectionDetailsTitle="intl-msg:vehicle_status.datatabs.dtc_diagnostic_memory"
                                totalCriticalItems={totalActiveDm4DtcsCritical}
                                totalWarningItems={totalActiveDm4DtcsWarning}
                                totalOtherItems={totalActiveDm4DtcsOther}
                            />
                        }
                    />
                )}
            {(deviceType === 'tbm3' || deviceType === 'cm4') &&
                selectedDataTabsSections.includes(VehicleStatusDataSections.DTC_ACTIVE_ERRORS) && (
                    <DataTab
                        tabKey="dtc-dm1-details-tab"
                        className="border border-left-only"
                        title={
                            <DataTabSectionDetails
                                dataTabSectionDetailsTitle="intl-msg:vehicle_status.datatabs.dtc_active_errors"
                                totalCriticalItems={totalActiveDm1DtcsCritical}
                                totalWarningItems={totalActiveDm1DtcsWarning}
                                totalOtherItems={totalActiveDm1DtcsOther}
                            />
                        }
                    />
                )}
        </DataTabs>
    );
};

const VehicleDataTabComponent = ({
    type,
    name,
    vin,
    customerName,
    licensePlate,
    deviceType,
    bookedProduct,
    totalAppointments,
    totalWorkshopActions,
    totalVehicleNotes,
    nextWorkshopAppointment,
    totalComponentsOverdue,
    totalComponentsAlmostDue,
    totalBrakeLiningsCritical,
    totalBrakeLiningsWarning,
    totalActiveDm1DtcsCritical,
    totalActiveDm1DtcsWarning,
    totalActiveDm1DtcsOther,
    totalActiveDm4DtcsCritical,
    totalActiveDm4DtcsWarning,
    totalActiveDm4DtcsOther,
    totalWarningMessagesCritical,
    totalWarningMessagesWarning,
    totalWarningMessagesInfo,
    lastMaintenanceMessageTimestamp,
    onClickVehicleNameAndVinAndLicensePlate,
    onClickCustomerName,
    onClickVehicleInfoSection,
    onClickMaintenanceSection,
    onClickBrakeLiningsSection,
    onClickWarningMessagesSection,
    onClickDtcDm1Section,
    onClickDtcDm4Section,
    syncStatus,
    status,
    highlight,
    clearHighlight,
    selectedDataTabsSections,
}: VehicleDataTabComponentProps) => {
    useEffect(() => {
        if (highlight) {
            setTimeout(() => clearHighlight(), HIGHLIGHT_DURATION_SECONDS * 1000);
        }
    }, [highlight]);

    return (
        <div className="display-flex flex-row">
            <div className={`display-flex margin-top-20 ${getStatusColorClassName(status)}`} style={{ minWidth: 4 }} />
            <div className={`display-flex flex-column flex-1-0 ${highlight && 'border-color-dark'}`}>
                <VehicleBasicDataComponent
                    type={type}
                    name={name}
                    bookedProduct={bookedProduct}
                    vin={vin}
                    customerName={customerName}
                    licensePlate={licensePlate}
                    lastMaintenanceMessageTimestamp={lastMaintenanceMessageTimestamp}
                    syncStatus={syncStatus}
                    onClickVehicleNameAndVinAndLicensePlate={onClickVehicleNameAndVinAndLicensePlate}
                    onClickCustomerName={onClickCustomerName}
                    highlight={highlight}
                />
                <VehicleDataComponent
                    deviceType={deviceType}
                    totalAppointments={totalAppointments}
                    totalWorkshopActions={totalWorkshopActions}
                    totalVehicleNotes={totalVehicleNotes}
                    nextWorkshopAppointment={nextWorkshopAppointment}
                    totalComponentsOverdue={totalComponentsOverdue}
                    totalComponentsAlmostDue={totalComponentsAlmostDue}
                    totalBrakeLiningsCritical={totalBrakeLiningsCritical}
                    totalBrakeLiningsWarning={totalBrakeLiningsWarning}
                    totalActiveDm1DtcsCritical={totalActiveDm1DtcsCritical}
                    totalActiveDm1DtcsWarning={totalActiveDm1DtcsWarning}
                    totalActiveDm1DtcsOther={totalActiveDm1DtcsOther}
                    totalActiveDm4DtcsCritical={totalActiveDm4DtcsCritical}
                    totalActiveDm4DtcsWarning={totalActiveDm4DtcsWarning}
                    totalActiveDm4DtcsOther={totalActiveDm4DtcsOther}
                    totalWarningMessagesCritical={totalWarningMessagesCritical}
                    totalWarningMessagesWarning={totalWarningMessagesWarning}
                    totalWarningMessagesInfo={totalWarningMessagesInfo}
                    onClickVehicleInfoSection={onClickVehicleInfoSection}
                    onClickMaintenanceSection={onClickMaintenanceSection}
                    onClickBrakeLiningsSection={onClickBrakeLiningsSection}
                    onClickWarningMessagesSection={onClickWarningMessagesSection}
                    onClickDtcDm1Section={onClickDtcDm1Section}
                    onClickDtcDm4Section={onClickDtcDm4Section}
                    highlight={highlight}
                    selectedDataTabsSections={selectedDataTabsSections}
                />
            </div>
        </div>
    );
};

export default VehicleDataTabComponent;
